import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import { handleApiError, type ErrorHandlerOptions, type ApiErrorContext } from './errorHandler';

/**
 * Refresh-token akışı konfigürasyonu. Verildiğinde 401 alan istekler önce
 * /api/auth/refresh ile access token yenilemeyi dener, başarılıysa istek
 * yeni token ile bir kez tekrarlanır; ancak refresh de başarısızsa
 * onUnauthorized (logout) tetiklenir.
 */
export interface TokenRefreshConfig {
  /** localStorage key of the access token (same key the bearer interceptor reads) */
  tokenKey: string;
  /** localStorage key of the refresh token */
  refreshTokenKey: string;
  /** Full URL of the refresh endpoint (POST { refreshToken } → { token, refreshToken }) */
  refreshUrl: string;
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retriedAfterRefresh?: boolean;
}

/**
 * Single-flight refresh: eşzamanlı 401'ler (ör. dashboard'un paralel widget
 * istekleri) tek refresh çağrısını paylaşır — endpoint "login" rate limit
 * policy'sinde (5/dk), istek başına refresh atılamaz.
 */
function createRefreshRunner(cfg: TokenRefreshConfig): () => Promise<string | null> {
  let inflight: Promise<string | null> | null = null;

  const run = async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem(cfg.refreshTokenKey);
    if (!refreshToken) return null;
    try {
      // Bare axios: client interceptor'larından geçmemeli (recursion/logout riski).
      const { data } = await axios.post<{ token: string; refreshToken: string }>(cfg.refreshUrl, {
        refreshToken,
      });
      localStorage.setItem(cfg.tokenKey, data.token);
      localStorage.setItem(cfg.refreshTokenKey, data.refreshToken);
      return data.token;
    } catch {
      // Refresh token geçersiz/süresi dolmuş — oturum gerçekten bitti.
      localStorage.removeItem(cfg.tokenKey);
      localStorage.removeItem(cfg.refreshTokenKey);
      return null;
    }
  };

  return () => {
    inflight ??= run().finally(() => {
      inflight = null;
    });
    return inflight;
  };
}

/**
 * Setup Bearer token authentication interceptor
 * Reads token from localStorage and adds to Authorization header
 */
export function setupBearerAuthInterceptor(
  client: AxiosInstance,
  tokenKey: string = 'token'
): void {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem(tokenKey);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
}

/**
 * Setup API Key authentication interceptor
 * Adds X-Api-Key header to all requests
 */
export function setupApiKeyAuthInterceptor(
  client: AxiosInstance,
  apiKey: string
): void {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      config.headers['X-Api-Key'] = apiKey;
      return config;
    },
    (error) => Promise.reject(error)
  );
}

/**
 * Login/refresh 401'i "oturum düştü" değildir — redirect + toast yanlış olur (audit M13).
 * Returns false for auth endpoints that should not trigger the unauthorized handler.
 */
export function shouldTriggerUnauthorizedHandler(url: string | undefined): boolean {
  if (!url) return true;
  return !/\/api\/auth\/(login|admin\/login|refresh)(\?|$)/.test(url);
}

/**
 * Setup error handling interceptor
 * Handles common HTTP errors (401, 403, 429, 500)
 */
export function setupErrorInterceptor(
  client: AxiosInstance,
  options?: ErrorHandlerOptions & { onUnauthorized?: () => void; refresh?: TokenRefreshConfig }
): void {
  const refreshOnce = options?.refresh ? createRefreshRunner(options.refresh) : null;

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const url = error.config?.url;

      // 401 + refresh konfigüre + henüz retry edilmemiş → token yenile, isteği tekrarla.
      // Refresh başarısızsa aşağıdaki normal 401 akışına düşer (toast + onUnauthorized).
      const originalConfig = error.config as RetryableRequestConfig | undefined;
      if (
        refreshOnce &&
        error.response?.status === 401 &&
        originalConfig &&
        !originalConfig._retriedAfterRefresh &&
        shouldTriggerUnauthorizedHandler(url)
      ) {
        const newToken = await refreshOnce();
        if (newToken) {
          originalConfig._retriedAfterRefresh = true;
          originalConfig.headers.Authorization = `Bearer ${newToken}`;
          return client.request(originalConfig);
        }
      }
      const context: ApiErrorContext = {
        url,
        method: error.config?.method?.toUpperCase(),
      };

      // Suppress the "Oturum süreniz doldu" toast for login/refresh 401s (audit M13):
      // a wrong password on /api/auth/login is not an expired session.
      const isAuthEndpoint401 =
        error.response?.status === 401 && !shouldTriggerUnauthorizedHandler(url);
      const effectiveOptions = isAuthEndpoint401
        ? { ...options, silent: true }
        : options;

      // Handle the error
      handleApiError(error, effectiveOptions, context);

      // Special handling for 401 - unauthorized (login/refresh hariç — audit M13)
      if (
        error.response?.status === 401 &&
        options?.onUnauthorized &&
        shouldTriggerUnauthorizedHandler(url)
      ) {
        options.onUnauthorized();
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Create a request logging interceptor (useful for debugging)
 */
export function setupRequestLoggingInterceptor(
  client: AxiosInstance,
  options?: { logBody?: boolean }
): void {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
      if (options?.logBody && config.data) {
        console.log('[API] Request body:', config.data);
      }
      return config;
    }
  );
}

/**
 * Create a response logging interceptor (useful for debugging)
 */
export function setupResponseLoggingInterceptor(
  client: AxiosInstance,
  options?: { logBody?: boolean }
): void {
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      console.log(`[API] ${response.status} ${response.config.url}`);
      if (options?.logBody && response.data) {
        console.log('[API] Response body:', response.data);
      }
      return response;
    }
  );
}
